"use client"

import * as React from "react"
import { useNavigate } from "react-router-dom"
import {
  FileText,
  Briefcase,
  Users,
  Upload,
  Plus,
  Settings,
  Sparkles,
  Scale,
  Mic2,
  Music,
  FileSignature,
  ArrowRight,
  LucideIcon,
} from "lucide-react"
import {
  Command,
  CommandCollection,
  CommandDialog,
  CommandDialogPopup,
  CommandEmpty,
  CommandFooter,
  CommandGroup,
  CommandGroupLabel,
  CommandInput,
  CommandItem,
  CommandList,
  CommandPanel,
  CommandSeparator,
  CommandShortcut,
} from "@/components/ui/Command"
import { ScrollArea } from "@/components/ui/scroll-area"
import { PATH_ROOT } from "@/routes/paths"
import { useUploadDocument } from "@/contexts/UploadDocumentContext"
import { useDemoDealsDocuments } from "@/hooks/deals/useDemoDealsDocuments"

// Search result types
type SearchResultType = "deal" | "contract" | "document" | "action" | "person"

interface SearchItem {
  value: string
  label: string
  type: SearchResultType
  icon: LucideIcon | React.ComponentType<React.SVGProps<SVGSVGElement>>
  url?: string
  description?: string
  meta?: string
}

interface SearchGroup {
  value: string
  items: SearchItem[]
}

// Map contract types to icons
const getContractIcon = (type: string) => {
  switch (type) {
    case "recording":
      return Mic2
    case "producer":
      return Music
    case "services":
      return FileSignature
    default:
      return FileText
  }
}

// Build search data from demo data
const buildSearchData = (demoDeals: any[], demoContractsData: any[], demoDocuments: any[]): SearchGroup[] => {
  const deals: SearchItem[] = demoDeals.slice(0, 6).map((deal) => ({
    value: `deal-${deal.id}`,
    label: deal.name,
    type: "deal" as const,
    icon: Briefcase,
    url: `${PATH_ROOT.demo.deal}/${deal.id}`,
    description: deal.type,
    meta: deal.status,
  }))

  const contracts: SearchItem[] = demoContractsData.map((contract) => ({
    value: `contract-${contract.contractId}`,
    label: contract.name,
    type: "contract" as const,
    icon: getContractIcon(contract.type),
    url: `${PATH_ROOT.demo.analysis}?id=${contract.contractId}`,
    description: contract.type,
    meta: contract.status,
  }))

  const documents: SearchItem[] = demoDocuments.slice(0, 4).map((doc) => ({
    value: `doc-${doc.id}`,
    label: doc.name,
    type: "document" as const,
    icon: FileText,
    description: doc.type,
    meta: doc.status === "signed" ? "Signed" : doc.status === "pending_review" ? "Pending" : "Draft",
  }))

  return [
    { value: "Deals", items: deals },
    { value: "Contracts", items: contracts },
    { value: "Documents", items: documents },
  ]
}

// Quick actions
const quickActions: SearchItem[] = [
  {
    value: "action-new-deal",
    label: "Create New Deal",
    type: "action",
    icon: Plus,
    url: PATH_ROOT.demo.newDeal,
    // description: "Start a new deal from scratch",
  },
  {
    value: "action-upload",
    label: "Upload Document",
    type: "action",
    icon: Upload,
    // description: "Upload a contract or document for analysis",
  },
  {
    value: "action-ai-analysis",
    label: "AI Contract Analysis",
    type: "action",
    icon: Sparkles,
    url: PATH_ROOT.demo.vault,
    // description: "Analyze contracts with AI",
  },
  {
    value: "action-compare",
    label: "Compare Contracts",
    type: "action",
    icon: Scale,
    url: PATH_ROOT.demo.comparison,
    // description: "Side-by-side contract comparison",
  },
  {
    value: "action-deal-compare",
    label: "Compare Offers (Deal Comparison)",
    type: "action",
    icon: Scale,
    url: PATH_ROOT.demo.dealComparison,
  },
]

interface SearchCommandProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function SearchCommand({ open, onOpenChange }: SearchCommandProps) {
  const navigate = useNavigate()
  const { openDialog: openUploadDialog } = useUploadDocument()
  const { demoDeals, demoDocuments, demoContracts } = useDemoDealsDocuments()
  const [searchValue, setSearchValue] = React.useState("")

  const searchData = React.useMemo(() => buildSearchData(demoDeals, demoContracts, demoDocuments), [demoDeals, demoContracts, demoDocuments])

  // Filter items based on search
  const filteredData = React.useMemo(() => {
    if (!searchValue.trim()) {
      return searchData
    }

    const query = searchValue.toLowerCase()
    return searchData
      .map((group) => ({
        ...group,
        items: group.items.filter(
          (item) =>
            item.label.toLowerCase().includes(query) ||
            item.description?.toLowerCase().includes(query)
        ),
      }))
      .filter((group) => group.items.length > 0)
  }, [searchData, searchValue])

  const filteredActions = React.useMemo(() => {
    if (!searchValue.trim()) {
      return quickActions
    }
    const query = searchValue.toLowerCase()
    return quickActions.filter(
      (item) =>
        item.label.toLowerCase().includes(query) ||
        item.description?.toLowerCase().includes(query)
    )
  }, [searchValue])

  // Flatten all items for the Command component
  const allItems = React.useMemo(() => {
    const items: SearchItem[] = []
    filteredData.forEach((group) => items.push(...group.items))
    items.push(...filteredActions)
    return items
  }, [filteredData, filteredActions])

  const handleSelect = (item: SearchItem) => {
    onOpenChange(false)
    setSearchValue("")

    if (item.value === "action-upload") {
      openUploadDialog()
      return
    }

    if (item.url) {
      navigate(item.url)
    }
  }

  const getStatusColor = (meta?: string) => {
    switch (meta?.toLowerCase()) {
      case "completed":
      case "signed":
      case "complete":
        return "text-emerald-500"
      case "in progress":
        return "text-blue-500"
      case "pending":
      case "pending_review":
        return "text-amber-500"
      default:
        return "text-muted-foreground"
    }
  }

  const hasResults = filteredData.length > 0 || filteredActions.length > 0

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <CommandDialogPopup>
        <Command items={allItems}>
          <CommandInput
            placeholder="Search deals, contracts, documents…"
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
          />
          <CommandPanel className="flex flex-col min-h-0 overflow-hidden">
            <ScrollArea className="max-h-80">
              <CommandList>
                {!hasResults && searchValue && (
                <CommandEmpty>
                  <div className="flex flex-col items-center gap-2 py-6">
                    <FileText className="size-10 text-muted-foreground/50" />
                    <p className="text-sm text-muted-foreground">No results found for "{searchValue}"</p>
                    <p className="text-xs text-muted-foreground/70">Try a different search term</p>
                  </div>
                </CommandEmpty>
              )}

              {/* Quick Actions */}
              {filteredActions.length > 0 && (
                <>
                  <CommandGroup items={filteredActions}>
                    <CommandGroupLabel>Quick Actions</CommandGroupLabel>
                    <CommandCollection>
                      {(item: SearchItem) => (
                        <CommandItem 
                          key={item.value} 
                          value={item}
                          onClick={() => handleSelect(item)}
                        >
                          <div className="flex items-center gap-3 w-full">
                            <div className="flex size-8 items-center justify-center rounded-lg dark:bg-white/5 text-primary">
                              <item.icon className="size-4" />
                            </div>
                            <div className="flex flex-col flex-1 min-w-0">
                              <span className="font-medium truncate">{item.label}</span>
                              {item.description && (
                                <span className="text-xs text-muted-foreground truncate">
                                  {item.description}
                                </span>
                              )}
                            </div>
                            <ArrowRight className="size-4 text-muted-foreground/50" />
                          </div>
                        </CommandItem>
                      )}
                    </CommandCollection>
                  </CommandGroup>
                  {filteredData.length > 0 && <CommandSeparator />}
                </>
              )}

              {/* Search Results */}
              {filteredData.map((group, index) => (
                <React.Fragment key={group.value}>
                  <CommandGroup items={group.items}>
                    <CommandGroupLabel>{group.value}</CommandGroupLabel>
                    <CommandCollection>
                      {(item: SearchItem) => (
                        <CommandItem 
                          key={item.value} 
                          value={item}
                          onClick={() => handleSelect(item)}
                        >
                          <div className="flex items-center gap-3 w-full">
                            <div className="flex size-8 items-center justify-center rounded-lg bg-muted">
                              <item.icon className="size-4 text-muted-foreground" />
                            </div>
                            <div className="flex flex-col flex-1 min-w-0">
                              <span className="font-medium truncate">{item.label}</span>
                              {item.description && (
                                <span className="text-xs text-muted-foreground truncate">
                                  {item.description}
                                </span>
                              )}
                            </div>
                            {item.meta && (
                              <span className={`text-xs font-medium ${getStatusColor(item.meta)}`}>
                                {item.meta}
                              </span>
                            )}
                          </div>
                        </CommandItem>
                      )}
                    </CommandCollection>
                  </CommandGroup>
                  {index < filteredData.length - 1 && <CommandSeparator />}
                </React.Fragment>
              ))}
              </CommandList>
            </ScrollArea>
          </CommandPanel>
          <CommandFooter>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-1.5">
                <kbd className="pointer-events-none inline-flex h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground">
                  <span className="text-xs">↑↓</span>
                </kbd>
                <span>Navigate</span>
              </div>
              <div className="flex items-center gap-1.5">
                <kbd className="pointer-events-none inline-flex h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground">
                  ↵
                </kbd>
                <span>Select</span>
              </div>
              <div className="flex items-center gap-1.5">
                <kbd className="pointer-events-none inline-flex h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground">
                  esc
                </kbd>
                <span>Close</span>
              </div>
            </div>
            <CommandShortcut>{/Mac|iPod|iPhone|iPad/.test(navigator.userAgent) ? "⌘" : "Ctrl+"}K</CommandShortcut>
          </CommandFooter>
        </Command>
      </CommandDialogPopup>
    </CommandDialog>
  )
}

// Hook for keyboard shortcut
export function useSearchCommand() {
  const [open, setOpen] = React.useState(false)

  React.useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault()
        setOpen((open) => !open)
      }
    }

    document.addEventListener("keydown", down)
    return () => document.removeEventListener("keydown", down)
  }, [])

  return { open, setOpen }
}

